import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, IdCard, Sparkles } from "lucide-react";
import SEO from "../../components/SEO";
import { REGISTRATION_LINKS } from "../../lib/constants";

export default function HowToGetCard() {
  return (
    <>
      <SEO
        title="How to Get a Medical Marijuana Card in DC (2026 Guide) | DWC"
        description="Step-by-step guide to getting a medical marijuana card in Washington, DC. Self-certify online in minutes — no doctor's visit required. DC and non-DC residents welcome."
        path="/how-to-get-medical-marijuana-card-dc"
        canonical="/blog/how-to-get-medical-marijuana-card-dc"
        ogType="article"
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: "How to Get a Medical Marijuana Card in DC", path: "/blog/how-to-get-medical-marijuana-card-dc" },
        ]}
        article={{
          publishedTime: "2026-05-12",
          modifiedTime: "2026-09-15",
          section: "Registration",
        }}
      />

      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">DC Patient Guide</div>
          <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            How to get a <em className="text-brand">medical marijuana card</em> in DC.
          </h1>
          <p className="mt-6 text-ink-muted text-lg max-w-2xl leading-relaxed">
            DC is one of the easiest jurisdictions in the country to access medical cannabis legally.
            You can self-certify online in minutes. Here's the full process — clear, current, and free of legalese.
          </p>
        </div>
      </section>

      <article className="py-20 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 prose prose-lg font-sans text-ink-muted">
          <h2 className="font-serif text-3xl text-ink mt-2 mb-5">The short version</h2>
          <p>
            In Washington, D.C., adults 21+ can self-certify for medical cannabis access through the
            Office of Cannabis Administration. No prescription. No physician sign-off required for
            self-certification. Once registered, you can shop at any licensed DC dispensary.
          </p>

          <h2 className="font-serif text-3xl text-ink mt-12 mb-5">If you're a DC resident</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Go to the official DC OCA registration portal.</li>
            <li>Complete the patient self-certification form online (around 5 minutes).</li>
            <li>Upload a valid DC ID.</li>
            <li>Pay the small registration fee.</li>
            <li>Receive your patient ID — you can now shop at DWC.</li>
          </ol>
          <p>
            <a href={REGISTRATION_LINKS.medical} target="_blank" rel="noreferrer" className="text-brand link-underline" data-testid="blog-register-medical-inline-1">
              Register for Medical Cannabis →
            </a>
          </p>

          <h2 className="font-serif text-3xl text-ink mt-12 mb-5">If you live outside DC (non-resident)</h2>
          <p>
            You have <strong>two options</strong>, and you only need one:
          </p>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Self-register as a temporary patient</strong> through the DC OCA portal — works for visitors and out-of-state cardholders alike.</li>
            <li><strong>Bring a valid medical cannabis ID</strong> from any U.S. state with a medical program. We accept it at checkout.</li>
          </ol>
          <p>
            <a href={REGISTRATION_LINKS.medical} target="_blank" rel="noreferrer" className="text-brand link-underline" data-testid="blog-register-medical-inline-2">
              Register for Medical Cannabis →
            </a>
          </p>

          <h2 className="font-serif text-3xl text-ink mt-12 mb-5">What you'll need before starting</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Government-issued photo ID</li>
            <li>Email address (for your patient ID)</li>
            <li>Credit/debit card for the registration fee</li>
            <li>~5 minutes</li>
          </ul>

          <h2 className="font-serif text-3xl text-ink mt-12 mb-5">Common questions</h2>
          <p><strong>Do I need to see a doctor?</strong> No — DC allows self-certification.</p>
          <p><strong>How long is my card valid?</strong> You can self-certify for periods ranging from short-term (3-day) to a full year, depending on the option you select at registration.</p>
          <p><strong>Can I use my DC card in other states?</strong> No. The DC patient program is recognized only inside the District.</p>
          <p><strong>Will my employer find out?</strong> Patient registration data is protected. Always check your employer's specific drug policies though.</p>

          <h2 className="font-serif text-3xl text-ink mt-12 mb-5">Once you're registered — visit DWC</h2>
          <p>
            We have a <Link to="/locations" className="text-brand link-underline">DC location on Georgia Ave</Link> with a curated menu —
            DWC Uptown at 4915 Georgia Ave NW. Same-day delivery is available across the District.
          </p>
        </div>
      </article>

      <section className="py-16 bg-ink text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Sparkles size={28} className="text-brand mx-auto mb-6" />
          <h2 className="font-serif text-4xl sm:text-5xl leading-[0.95]">
            Ready to <em className="text-brand">register</em>?
          </h2>
          <p className="mt-5 text-white/70 max-w-xl mx-auto">
            Pick your path below — both take about 5 minutes.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={REGISTRATION_LINKS.medical} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="blog-cta-register-medical">
              <IdCard size={14} /> Register for Medical Cannabis
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
